import React, { useState, useEffect, useCallback } from "react"; 
import { useSelector, useDispatch } from "react-redux";
import { setActivePage } from "../../features/navigation/navigationSlice";
import { Link, useNavigate } from "react-router-dom";
import { useTheme, Divider, Box, Collapse, Typography, Tooltip } from "@mui/material";
import './sidebar.css'
import styles from '../../styles/sidebar/index.module.scss'
import { breakpoints } from "../../theme/theme";
// utilities
import RoleEnum from "../../enums/RoleEnum";
import PermissionEnum from "../../enums/PermissionEnum";
import { ScaleEnum } from "../../enums/enumRegistry";
import logo_primary from "../../assets/images/logo.svg";
import logo_secondary from "../../assets/images/APLogo.svg";
import DEFAULT_ROUTES from "../../constants/routes"; 
import { getTrialUsage } from "../../utils/api/trialUsage_apis";
import FeedbackModal from '../FeedbackModal';
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    ChevronDownIcon,
    FolderIcon,
    SparklesIcon,
    UsersIcon,
    Cog6ToothIcon,
    UserPlusIcon,
    ShieldCheckIcon,
    BuildingOffice2Icon,
    CurrencyDollarIcon,
    ClipboardDocumentListIcon,
    UserGroupIcon,
    VariableIcon,
    PlayIcon,
    DocumentTextIcon,
    ArrowPathIcon,
    ChartBarIcon,
    DevicePhoneMobileIcon,
    ChatBubbleLeftEllipsisIcon,
    SquaresPlusIcon,
} from '@heroicons/react/24/outline';

// NavItem component for reusability
const NavItem = ({
  icon,
  text,
  active = false,
  onClick,
  isExpanded,
  isSubMenu = false,
  hasSubmenu = false,
  isSubmenuOpen = false,
  onToggleSubmenu,
  isMainNav = false,
  compactCollapsed = false,
  sx,
}) => {
  const isAction = typeof onClick === 'function';

  return (
    <Box
      component={isAction ? 'button' : 'div'}
      type={isAction ? 'button' : undefined}
      onClick={onClick}
      aria-label={isAction && !isExpanded ? text : undefined}
      className={`${styles['nav-item']} ${
        active ? styles['nav-item--active'] : ''
      } ${
        isExpanded ? styles['nav-item--expanded'] : styles['nav-item--collapsed']
      } ${compactCollapsed ? styles['nav-item--collapsed-compact'] : ''}`}
      style={sx}
    >
      <Box
        className={`${styles['nav-icon']} ${
          isSubMenu ? styles['nav-icon--submenu'] : styles['nav-icon--main']
        } ${
          isExpanded ? styles[`nav-icon--${isSubMenu ? 'submenu' : 'main'}--expanded`] : styles[`nav-icon--${isSubMenu ? 'submenu' : 'main'}--collapsed`]
        } ${
          active ? styles[`nav-icon--${isSubMenu ? 'submenu' : 'main'}--active`] : ''
        }`}
      >
        {icon}
      </Box>
      {isExpanded && (
        <Typography
          className={`${styles['nav-text']} ${
            isSubMenu ? styles['nav-text--submenu'] : styles['nav-text--main']
          } ${
            active ? styles[`nav-text--${isSubMenu ? 'submenu' : 'main'}--active`] : ''
          }`}
        >
          {text}
        </Typography>
      )}
      {hasSubmenu && isExpanded && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onToggleSubmenu();
          }}
          className={`${styles['nav-chevron']} ${
            active ? styles['nav-chevron--active'] : ''
          }`}
        >
          {isSubmenuOpen ? <ChevronDownIcon className="icon" /> : <ChevronRightIcon className="icon" />}
        </Box>
      )}
    </Box>
  );
};

// Function to check if the user has the permission to manage current project team
const hasManageProjectUsersPermission = (user, projectId) => {
  if (!user || !projectId) return false;

  const globalPermissions = user?.permissions || [];
  const consolidatedPermissions = user?.project_roles_permissions?.consolidatedPermissions || {};
  const projectPermissions = consolidatedPermissions[projectId];
  if(globalPermissions?.includes(PermissionEnum.UPDATE_PROJECT.name)|| projectPermissions?.includes(PermissionEnum.UPDATE_PROJECT.name)){
    return true
  }
  return false
};

const handleGenerateAiPermission = (user , projectId)=>{
if(!user ) return false;

const globalPermissions = user?.permissions || [];
const consolidatedPermissions = user?.project_roles_permissions?.consolidatedPermissions || {};
const projectPermissions = consolidatedPermissions[projectId];
if(globalPermissions?.includes(PermissionEnum.AI_GENERATE_TEST_CASE.name)|| projectPermissions?.includes(PermissionEnum.AI_GENERATE_TEST_CASE.name)){
  return true 
}

return false
}

export default function Sidebar({ onToggle }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const activePage = useSelector((state) => state.navigation.activePage);
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const [isExpanded, setIsExpanded] = useState(true);
  const permissions = useSelector(
    (state) => state.auth.user?.permissions || []
  );
  const user = useSelector((state) => state.auth.user);
  const userRole = useSelector((state) => state.auth.user?.roles);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [trialUsage, setTrialUsage] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const navigate = useNavigate();

  const isTrial = user?.plan_name === 'trial';

  const fetchTrialUsage = useCallback(() => {
    if (!isTrial) return;
    getTrialUsage()
      .then((res) => {
        if (res.status === 'success' && res.data) {
          setTrialUsage(res.data);
        }
      })
      .catch(() => setTrialUsage(null));
  }, [isTrial]);

  useEffect(() => {
    if (!isTrial) return;
    let mounted = true;
    getTrialUsage()
      .then((res) => {
        if (mounted && res.status === 'success' && res.data) {
          setTrialUsage(res.data);
        }
      })
      .catch(() => {
        if (mounted) setTrialUsage(null);
      });
    return () => {
      mounted = false;
    };
  }, [isTrial]);

  useEffect(() => {
    const onRefresh = () => fetchTrialUsage();
    window.addEventListener('trialUsageRefresh', onRefresh);
    return () => window.removeEventListener('trialUsageRefresh', onRefresh);
  }, [fetchTrialUsage]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
    onToggle(!isExpanded);
  };

  const handleNavigation = (page, page_name) => {
    // Prevent navigation if already on the active page
    if (effectiveActivePage === page_name) {
      return;
    }
    dispatch(setActivePage(page_name));
    navigate(`${DEFAULT_ROUTES.BASE_URL}/${page}`);
  };

  const toggleSubmenu = (itemId) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Create a minimal state object for renderCondition functions
  const state = {
    projects: { selectedProject },
    auth: { user }
  };

  // Function to determine if a section should be rendered
  const shouldRenderSection = (section) => {
    if (section.alwaysRender) return true;
    if (section.renderCondition) return section.renderCondition(state);
    return false;
  };

  // Function to determine if an item should be hidden
  const isItemHidden = (item) => {
    if (item.hidden === undefined) return false;
    if (typeof item.hidden === "function") return item.hidden(permissions);
    return item.hidden;
  };

  // mapping for effective active page
  const pageToNavPageMap = {
    'Testrun Details': 'Test Runs',
  };
  const effectiveActivePage = pageToNavPageMap[activePage] || activePage;

    const sidebarSections = [
    {
      id: "main",
      title: null,
      alwaysRender: true,
      items: [
        {
          id: "projects",
          text: "All Projects",
          icon: <FolderIcon className="icon" />,
          pageName: "Projects",
          route: DEFAULT_ROUTES.PROJECT.slice(1),
          isMainNav: true, // Mark as main navigation item
        },
      ]
    },
    {
      id: "divider1",
      type: "divider",
      alwaysRender: false,
      renderCondition: (state) => state.projects.selectedProject !== null,
      items: [{ id: "divider1-item", type: "divider" }]
    },
    {
      id: "activeProject",
      type: "activeProject",
      alwaysRender: false,
      renderCondition: (state) => state.projects.selectedProject !== null,
      items: []
    },
    {
      id: "testing",
      // title: "TESTING",
      alwaysRender: false,
      renderCondition: (state) => state.projects.selectedProject !== null,
      items: [
        {
          id: "testCases",
          text: "Test Cases",
          icon: <ClipboardDocumentListIcon className="icon" />,
          pageName: "Test Cases",
          route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.TEST_CASES}`,
          hidden: false,
        },
        {
          id: "testGroups",
          text: "Test Groups",
          icon: <UserGroupIcon className="icon" />,
          pageName: "Test Groups",
          route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.TEST_GROUPS}`,
          hidden: false,
        },
        // {
        //   id: "testRuns",
        //   text: "Test Runs",
        //   icon: <PlayIcon style={{ width: 20, height: 20 }} />,
        //   pageName: "Test Runs",
        //   route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}/test-runs`,
        //   hidden: false,
        // }, 
        {
          id: "variablesAndEnvironment",
          text: "Variables & Environment",
          icon: <VariableIcon className="icon" />,
          pageName: "Variables & Environment",
          route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.VARIABLES_AND_ENVIRONMENT}`,
          hidden: false,
        },
        {
          id: "fileManager",
          text: "File Manager",
          icon: <FolderIcon className="icon" />,
          pageName: "File Manager",
          route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.FILE_MANAGER}`,
          hidden: false,
        },
        // {
        //   id: "automation",
        //   text: "Automation",
        //   icon: <PlayIcon className="icon" />,
        //   hasSubmenu: true,
        //   isMainNav: true, // Mark as main navigation item
        //   subItems: [
        //     {
        //       id: "executionRecords",
        //       text: "Script Creator",
        //       icon: <DocumentTextIcon className="icon" />,
        //       pageName: "Script Creator",
        //       route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.AUTOMATION}`,
        //       hidden: false,
        //     },
        //     {
        //       id: "",
        //       text: "Script Manager",
        //       icon: <ArrowPathIcon className="icon" />,
        //       pageName: "Script Manager",
        //       route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.EXECUTION_REPLAY}`,
        //       hidden: false,
        //     },
        //     {
        //       id: "automationStudio",
        //       text: "Automation Studio",
        //       icon: <SparklesIcon className="icon" />,
        //       pageName: "Automation Studio",
        //       route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.AUTOMATION_STUDIO}`,
        //       hidden: false,
        //     },
        //   ],
        // },
        {
          id: "automationStudio",
          text: "Automation Studio",
          icon: <SparklesIcon className="icon" />,
          pageName: "Automation Studio",
          route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.AUTOMATION_STUDIO}`,
          hidden: false,
        },
        {
          id: "aiGenerator",
          text: "AI Generator",
          icon: <SquaresPlusIcon  className="icon" />,
          pageName: "AI Generator",
          route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.AI_GENERATOR}`,
          hidden: () => !handleGenerateAiPermission(user, selectedProject?.id)
        },
        { 
          id: "projectUsers",
          text: "Team", 
          icon: <UsersIcon className="icon" />,
          pageName: "Project Team",
          route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${selectedProject?.id}${DEFAULT_ROUTES.USERS}`,
          hidden: () => userRole?.includes(RoleEnum.TRIAL_USER.name) || !hasManageProjectUsersPermission(user, selectedProject?.id)
        },
        // { 
        //   id: "userApps",
        //   text: " App Integration", 
        //   icon: <UsersIcon className="icon" />,
        //   pageName: "App Integration",
        //   route: `${DEFAULT_ROUTES.PROJECT.slice(1)}/${
        //     selectedProject?.id
        //   }${DEFAULT_ROUTES.USER_APPS}`,
        //   hidden: () => false
        // },
      ]
    },
    {
      id: "divider2",
      type: "divider",
      alwaysRender: true,
      items: [{ id: "divider2-item", type: "divider" }]
    },
    {
      id: "admin",
      // title: "ADMIN",
      alwaysRender: false,
      renderCondition: (state) => {
        const roles = state.auth.user?.roles || [];
        if (roles.includes(RoleEnum.TRIAL_USER.name)) return false;
        const userPermission = state.auth.user?.permissions;
        return (userPermission?.includes(PermissionEnum.GET_AI_MODEL.name) || userPermission?.includes(PermissionEnum.GET_USER.name) || userPermission?.includes(PermissionEnum.GET_ROLES.name)) ?? false;
      },
      items: [
        {
          id: "settings",
          text: "Administration",
          icon: <Cog6ToothIcon className="icon" />,
          hasSubmenu: true,
          isMainNav: true, // Mark as main navigation item
          subItems: [
            {
              id: "manageUsers",
              text: "Manage Users",
              icon: <UsersIcon className="icon-medium" />,
              pageName: "Users",
              route: DEFAULT_ROUTES.USERS.slice(1),
              hidden: () => {
                const userPermission = user?.permissions;
                return !(userPermission?.includes(PermissionEnum.GET_USER.name));
              }
            },
            {
              id: "manageRoles",
              text: "Manage Roles",
              icon: <ShieldCheckIcon className="icon-medium" />,
              pageName: "Roles",
              route: DEFAULT_ROUTES.ROLES.slice(1),
              hidden: () => {
                if (userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name)) return true;
                const userPermission = user?.permissions;
                return !(userPermission?.includes(PermissionEnum.GET_ROLES.name));
              }
            },
            {
              id: "modelConfiguration",
              text: "Configuration",
              icon: <UserPlusIcon className="icon-medium" />,
              pageName: "Configuration",
              route: DEFAULT_ROUTES.CONFIGURATION.slice(1),
              hidden: () => {
                if (userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name)) return true;
                const userPermission = user?.permissions;
                return !(userPermission?.includes(PermissionEnum.GET_AI_MODEL.name));
              }
            },
            {
              id: "aiCostTracking",
              text: "AI Cost Tracking",
              icon: <CurrencyDollarIcon className="icon-medium" />,
              pageName: "AI Cost Tracking",
              route: DEFAULT_ROUTES.AI_COST_TRACKING.slice(1),
              hidden: () => userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name) || !userRole?.includes(RoleEnum.SUPER_ADMIN.name),
            },
            {
              id: "organizations",
              text: "Organizations",
              icon: <BuildingOffice2Icon className="icon-medium" />,
              pageName: "Organizations",
              route: DEFAULT_ROUTES.ORGANIZATIONS.slice(1),
              hidden: () => userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name) || !userRole?.includes(RoleEnum.SUPER_ADMIN.name),
            },
            {
              id: "creditManagement",
              text: "Credit Management",
              icon: <CurrencyDollarIcon className="icon-medium" />,
              pageName: "Credit Management",
              route: DEFAULT_ROUTES.SUPER_ADMIN_CREDITS.slice(1),
              hidden: () => userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name) || !userRole?.includes(RoleEnum.SUPER_ADMIN.name),
            },
            {
              id: "planManagement",
              text: "Plan Management",
              icon: <ClipboardDocumentListIcon className="icon-medium" />,
              pageName: "Plan Management",
              route: DEFAULT_ROUTES.PLAN_MANAGEMENT.slice(1),
              hidden: () => userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name) || !userRole?.includes(RoleEnum.SUPER_ADMIN.name),
            },
            {
              id: "usageByUser",
              text: "Usage by User",
              icon: <ChartBarIcon className="icon-medium" />,
              pageName: "Usage by User",
              route: DEFAULT_ROUTES.USAGE_BY_USER.slice(1),
              hidden: () => userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name) || !userRole?.includes(RoleEnum.SUPER_ADMIN.name),
            },
            // {
            //   id: "apps",
            //   text: "Apps",
            //   icon: <DevicePhoneMobileIcon className="icon-medium" />,
            //   pageName: "Apps",
            //   route: DEFAULT_ROUTES.APPS.slice(1),
            //   hidden: () => !userRole?.includes(RoleEnum.SUPER_ADMIN.name)
            // },
            {
              id: "feedback",
              text: "Feedback",
              icon: <ChatBubbleLeftEllipsisIcon className="icon-medium" />,
              pageName: "User Feedback",
              route: DEFAULT_ROUTES.FEEDBACK.slice(1),
              hidden: () => userRole?.includes(RoleEnum.TRIAL_ORG_ADMIN.name) || !userRole?.includes(RoleEnum.SUPER_ADMIN.name),
            },
          ],
        },
      ],
    },
  ];

  // Auto-open submenus when their child items are active
  useEffect(() => {
    sidebarSections.forEach(section => {
      section.items.forEach(item => {
        if (item.hasSubmenu && item.subItems) {
          const hasActiveSubItem = item.subItems.some(
            subItem => effectiveActivePage === subItem.pageName
          );
          
          if (hasActiveSubItem) {
            setOpenSubmenus(prev => ({
              ...prev,
              [item.id]: true
            }));
          }
        }
      });
    });
  }, [effectiveActivePage]);

  return (
    <Box
      className={`${styles['sidebar-container']} ${
        isExpanded ? styles['sidebar-container--expanded'] : styles['sidebar-container--collapsed']
      }`}
    >
      {/* Logo Section */}
      <Box
        className={`${styles['logo-section']} ${
          isExpanded ? styles['logo-section--expanded'] : styles['logo-section--collapsed']
        }`}
      >
        {isExpanded ? (
          <Typography
            className={styles['logo-text']}
          >
            QA Path
          </Typography>
        ) : (
          <img
            src={logo_secondary}
            alt="QA Path Logo"
            className={styles['logo-image']}
          />
        )}
      </Box>



      {/* Navigation Sections */}
      <Box className='scroll-container' sx={{ flex: 1, overflowY: 'auto' }}>
        {sidebarSections.map(
          (section, idx) =>
            shouldRenderSection(section) && (
              <Box key={section.id} sx={{ 
                ml: (section.id === "testing" && isExpanded) ? 1.4 : 0 
              }}>
                {/* Section Title */}
                {section.title && isExpanded && (
                  <Typography
                    variant="caption"
                    className={styles['section-title']}
                  >
                    {section.title}
                  </Typography>
                )}

                {/* Active Project Section */}
                {section.type === "activeProject" && selectedProject && isExpanded && (
                  <Box className={styles['active-project-section']}>
                    <Box className={styles['project-info']}>
                      <Box
                        className={`icon-cover-medium bg-primary ${styles['project-icon']}`}
                      >
                        <FolderIcon className="icon" style={{ color: theme.palette.customSidebar.white }} />
                      </Box>
                      <Box>
                        <Typography className={styles['project-name']}>
                          {selectedProject.name}
                        </Typography>
                        <Box className={styles['project-status']}>
                          <Box className={styles['status-indicator']} />
                          <Typography className={styles['status-text']}>
                            Active Project
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Section Items */}
                {section.items.map(
                  (item) =>
                    !isItemHidden(item) && ( 
                      <Box key={item.id}>
                        {item.type === "divider" ? (
                          <Divider
                            className={`${styles['section-divider']} ${
                              isExpanded ? styles['section-divider--expanded'] : ''
                            }`}
                          />
                        ) : (
                          isExpanded ? (
                            item.hasSubmenu ? (
                                <NavItem
                                  icon={item.icon}
                                  text={item.text}
                                  active={effectiveActivePage === item.pageName}
                                  onClick={() => {
                                    if (!isExpanded) {
                                      setIsExpanded(true);
                                      onToggle(true);
                                    } else {
                                      toggleSubmenu(item.id);
                                    }
                                  }}
                                  isExpanded={isExpanded}
                                  hasSubmenu={item.hasSubmenu}
                                  isSubmenuOpen={openSubmenus[item.id]}
                                  onToggleSubmenu={() => toggleSubmenu(item.id)}
                                  isMainNav={item.isMainNav}
                                />
                            ) : (
                              <Link 
                                to={`${DEFAULT_ROUTES.BASE_URL}/${item.route}`}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                                onClick={(e) => {
                                  // Prevent navigation if already on the active page
                                  if (effectiveActivePage === item.pageName) {
                                    e.preventDefault();
                                    return;
                                  }
                                  dispatch(setActivePage(item.pageName));
                                }}
                              >
                                <NavItem
                                  icon={item.icon}
                                  text={item.text}
                                  active={effectiveActivePage === item.pageName}
                                  isExpanded={isExpanded}
                                  isMainNav={item.isMainNav}
                                />
                              </Link>
                            )
                          ) : (
                            <Tooltip title={item.text} placement="right" arrow>
                              <div>
                                {item.hasSubmenu ? (
                                  <NavItem
                                    icon={item.icon}
                                    text={item.text}
                                    active={effectiveActivePage === item.pageName}
                                    onClick={() => {
                                      setIsExpanded(true);
                                      onToggle(true);
                                    }}
                                    isExpanded={isExpanded}
                                    hasSubmenu={item.hasSubmenu}
                                    isSubmenuOpen={openSubmenus[item.id]}
                                    onToggleSubmenu={() => toggleSubmenu(item.id)}
                                    isMainNav={item.isMainNav}
                                  />
                                ) : (
                                  <Link 
                                    to={`${DEFAULT_ROUTES.BASE_URL}/${item.route}`}
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                    onClick={(e) => {
                                      // Prevent navigation if already on the active page
                                      if (effectiveActivePage === item.pageName) {
                                        e.preventDefault();
                                        return;
                                      }
                                      dispatch(setActivePage(item.pageName));
                                    }}
                                  >
                                    <NavItem
                                      icon={item.icon}
                                      text={item.text}
                                      active={effectiveActivePage === item.pageName}
                                      isExpanded={isExpanded}
                                      isMainNav={item.isMainNav}
                                    />
                                  </Link>
                                )}
                              </div>
                            </Tooltip>
                          )
                        )}

                        {/* Render Subitems if applicable */}
                        {item.hasSubmenu && item.subItems && (
                          <Collapse in={openSubmenus[item.id] && isExpanded}>
                            <Box className={styles['submenu-container']}>
                              {item.subItems.map(
                                (subItem, index) =>
                                  !isItemHidden(subItem) && (
                                    <Box
                                      key={subItem.id}
                                      className={`${styles['submenu-item']} ${
                                        openSubmenus[item.id] && isExpanded 
                                          ? styles['submenu-item--animated'] 
                                          : ''
                                      }`}
                                      style={{
                                        animationDelay: `${index * 0.1}s`
                                      }}
                                    >
                                      <Link 
                                        to={`${DEFAULT_ROUTES.BASE_URL}/${subItem.route}`}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                        onClick={(e) => {
                                          // Prevent navigation if already on the active page
                                          if (effectiveActivePage === subItem.pageName) {
                                            e.preventDefault();
                                            return;
                                          }
                                          dispatch(setActivePage(subItem.pageName));
                                        }}
                                      >
                                        <NavItem
                                          icon={subItem.icon}
                                          text={subItem.text}
                                          active={effectiveActivePage === subItem.pageName}
                                          isExpanded={isExpanded}
                                          isSubMenu={true}
                                          isMainNav={false}
                                        />
                                      </Link>
                                    </Box>
                                  )
                              )}
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    )
                )}

              </Box>
            )
        )}
      </Box>

      {/* Trial credits summary (bottom-left sidebar) */}
      {isTrial && trialUsage && Number(trialUsage.credit_limit) > 0 && !trialUsage.is_expired && (
        <Box sx={{ px: isExpanded ? 2 : 1, py: 1, borderTop: '1px solid rgba(148, 163, 184, 0.3)' }}>
          <Typography
            variant="caption"
            sx={{
              color: '#E5E7EB',
              display: 'block',
              textAlign: isExpanded ? 'left' : 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Credits remaining: {Number(trialUsage.credits_remaining ?? 0).toFixed(2)}
          </Typography>
        </Box>
      )}

      {/* Share Feedback — hidden for super_admin (they use the admin Feedback page) */}
      {/* !userRole?.includes(RoleEnum.SUPER_ADMIN.name) && */ (
        <>
          <div className={styles['share-feedback-section']}>
            {isExpanded ? (
              <NavItem
                icon={<ChatBubbleLeftEllipsisIcon className="icon" />}
                text={'Share Feedback'}
                onClick={() => setFeedbackOpen(true)}
                isExpanded={isExpanded}
                isMainNav
                compactCollapsed
              />
            ) : (
              <Tooltip title="Share Feedback" placement="right" arrow>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <NavItem
                    icon={<ChatBubbleLeftEllipsisIcon className="icon" />}
                    text={'Share Feedback'}
                    onClick={() => setFeedbackOpen(true)}
                    isExpanded={isExpanded}
                    isMainNav
                    compactCollapsed
                  />
                </div>
              </Tooltip>
            )}
          </div>
          {feedbackOpen && (
            <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
          )}
        </>
      )}

      {/* Collapse Button */}
      <Box
        className={styles['collapse-button']}
        onClick={toggleSidebar}
      >
        {isExpanded ? <ChevronLeftIcon className="icon" /> : <ChevronRightIcon className="icon" />}
      </Box>
    </Box>
  );
}
